import { forwardRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { usePopper } from "react-popper";
import { t } from "@/i18n-js/instance";
import type { LocationType, LocationValue } from "@/react/hooks/useMapbox";
import { useMapbox } from "@/react/hooks/useMapbox";
import { Combobox } from "@circle-react-uikit/Combobox";
import type { FormInputProps } from "@circle-react-uikit/Form/FormInput";
import { popperSameWidthModifier } from "../../Dropdown/utils";
import { SearchableSelect } from "../../SearchableSelect";
import { OptionsList } from "./OptionsList";

interface LocationInputProps extends FormInputProps {
  initialValue?: LocationValue;
  inputPlaceholder?: string;
  locationTypesToSearch?: LocationType[];
  shouldShowClearButton?: boolean;
  shouldSkipLocationDetailRetrieve?: boolean;
}

export const LocationInput = forwardRef<HTMLInputElement, LocationInputProps>(
  props => {
    const {
      name,
      shouldSkipLocationDetailRetrieve,
      locationTypesToSearch,
      className,
      inputPlaceholder,
      initialValue,
      shouldShowClearButton = true,
    } = props;

    const { getFieldState } = useFormContext();
    const [searchValue, setSearchValue] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const {
      locationOptions,
      handleSuggestionSelect,
      value,
      debouncedSearch,
      displayValue,
      hasNoSuggestions,
      handleClearLocation,
    } = useMapbox({
      name,
      initialValue,
      shouldSkipLocationDetailRetrieve,
      locationTypes: locationTypesToSearch,
    });

    const handleSearchChange = (value: string) => {
      setSearchValue(value);
      void debouncedSearch(value);
    };

    const [targetElement, setTargetElement] = useState<HTMLDivElement | null>(
      null,
    );
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
      null,
    );
    const { styles, attributes, forceUpdate } = usePopper(
      targetElement,
      popperElement,
      {
        placement: "bottom",
        strategy: "fixed",
        modifiers: [popperSameWidthModifier],
      },
    );

    return (
      <div className={className}>
        <Combobox value={value} onChange={handleSuggestionSelect}>
          {({ open }: { open: boolean }) => {
            if (open !== isOpen) {
              setIsOpen(open);
              forceUpdate?.();
            }

            return (
              <>
                <Combobox.InputWrapper
                  ref={setTargetElement}
                  isError={!!getFieldState(name).error}
                >
                  <Combobox.Input
                    displayValue={displayValue}
                    onChange={event => handleSearchChange(event.target.value)}
                    placeholder={
                      inputPlaceholder ??
                      t("filters.location_filter.search_location")
                    }
                  />
                  {shouldShowClearButton && !!value?.text && (
                    <Combobox.ClearButton onClick={handleClearLocation} />
                  )}
                </Combobox.InputWrapper>
                <div
                  className="z-10"
                  ref={setPopperElement}
                  style={styles.popper}
                  {...attributes.popper}
                >
                  <Combobox.Options>
                    <Combobox.OptionsScrollableContainer>
                      {!locationOptions?.length ? (
                        <SearchableSelect.EmptyOption
                          label={
                            !searchValue
                              ? t("filters.location_filter.search_helper")
                              : hasNoSuggestions
                                ? t(
                                    "filters.location_filter.no_locations_found",
                                  )
                                : ""
                          }
                        />
                      ) : (
                        <OptionsList locationOptions={locationOptions} />
                      )}
                    </Combobox.OptionsScrollableContainer>
                  </Combobox.Options>
                </div>
              </>
            );
          }}
        </Combobox>
      </div>
    );
  },
);

LocationInput.displayName = "LocationInput";
